import React from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight, Building2, Home, Briefcase, Building, Factory, GraduationCap, Hotel, ShoppingBag, Truck, Cog, Landmark,
  QrCode, Cloud, Smartphone, Code2, Tag, Radio, Activity, BarChart3, Users, Bell, PackageSearch, LayoutDashboard,
} from "lucide-react";
import { Placeholder, Media, Reveal, SectionLabel } from "./Placeholder";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { useLang } from "../i18n";
import { IMG, PRODUCTS, CASE_IMAGES } from "../assets";
import { scrollToId as scrollTo } from "../lenis";

/* ---------------- PRODUCTS ---------------- */
export function Products() {
  const { t } = useLang();
  return (
    <section id="products" className="bg-white py-24 sm:py-32" data-testid="products-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-16 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div className="max-w-2xl">
            <SectionLabel>{t.products.label}</SectionLabel>
            <h2 className="font-sans text-4xl font-extrabold leading-tight tracking-tight text-ink sm:text-5xl lg:text-6xl">
              {t.products.title}
            </h2>
          </div>
          <p className="max-w-sm font-body text-lg leading-relaxed text-ink/60">{t.products.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {t.products.cards.map((c, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08} className="group flex flex-col overflow-hidden rounded-lg border border-border bg-white transition-all duration-300 hover:-translate-y-1.5 hover:border-ink hover:shadow-[8px_8px_0px_#0A0A0A]" >
              <div className="p-3">
                {PRODUCTS[i]
                  ? <Media src={PRODUCTS[i]} alt={c.n} ratio="4/3" fit="contain" bg="light" className="w-full" />
                  : <Placeholder label={c.n.toUpperCase()} ratio="4/3" tone="light" className="w-full" />}
              </div>
              <div className="flex flex-1 flex-col p-6 pt-3">
                <h3 className="font-grotesk text-xl font-bold text-ink">{c.n}</h3>
                <p className="mt-2 flex-1 font-body text-sm leading-relaxed text-ink/60">{c.d}</p>
                <button onClick={() => scrollTo("contact")} className="mt-5 inline-flex items-center gap-1.5 self-start font-grotesk text-sm font-bold text-electric-blue transition-colors group-hover:text-ink" data-testid={`product-cta-${i}`}>
                  {t.products.btn}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- SECTORS ---------------- */
const sectorIcons = [Building2, Home, Briefcase, Building, Factory, GraduationCap, Hotel, ShoppingBag, Truck, Cog, Landmark];
export function Sectors() {
  const { t } = useLang();
  return (
    <section id="sectors" className="bg-ink py-24 text-white sm:py-32" data-testid="sectors-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionLabel dark>{t.sectors.label}</SectionLabel>
          <h2 className="font-sans text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            {t.sectors.title}
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-white/10 bg-white/10 sm:grid-cols-3 lg:grid-cols-4">
          {t.sectors.items.map((s, i) => {
            const Icon = sectorIcons[i] || Building2;
            return (
              <Reveal key={i} delay={(i % 4) * 0.05} className="group flex items-center gap-4 bg-ink p-6 transition-colors hover:bg-electric-blue">
                <Icon className="h-7 w-7 shrink-0 text-lime-accent transition-colors group-hover:text-white" strokeWidth={1.5} />
                <span className="font-grotesk text-base font-semibold">{s}</span>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FEATURES (bento) ---------------- */
const featureIcons = [QrCode, Cloud, Smartphone, Code2, Tag, Radio, Activity, BarChart3, Users, Bell, PackageSearch, LayoutDashboard];
export function Features() {
  const { t } = useLang();
  return (
    <section className="bg-white py-24 sm:py-32" data-testid="features-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionLabel>{t.features.label}</SectionLabel>
          <h2 className="font-sans text-4xl font-extrabold leading-tight tracking-tight text-ink sm:text-5xl lg:text-6xl">
            {t.features.title}
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {t.features.items.map((f, i) => {
            const Icon = featureIcons[i] || QrCode;
            const highlight = i === 0 || i === 7;
            return (
              <Reveal key={i} delay={(i % 4) * 0.05} className={`group rounded-lg border p-6 transition-all duration-300 hover:-translate-y-1 ${highlight ? "border-transparent bg-lime-accent" : "border-border bg-white hover:border-ink"}`}>
                <Icon className={`h-7 w-7 ${highlight ? "text-ink" : "text-electric-blue"}`} strokeWidth={1.5} />
                <h3 className="mt-6 font-grotesk text-base font-bold text-ink">{f}</h3>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- WHY (stats) ---------------- */
export function Why() {
  const { t } = useLang();
  return (
    <section className="bg-electric-blue py-24 text-white sm:py-32" data-testid="why-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionLabel dark>{t.why.label}</SectionLabel>
          <h2 className="font-sans text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            {t.why.title}
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-white/20 bg-white/20 lg:grid-cols-3">
          {t.why.stats.map((s, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08} className="bg-electric-blue p-8">
              <div className="font-display text-6xl font-black text-lime-accent sm:text-7xl">{s.v}</div>
              <p className="mt-3 font-grotesk text-base font-semibold text-white/90">{s.l}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- GALLERY ---------------- */
const galleryItems = [
  { l: "GALLERIA 1 — OUTDOOR", r: "4/3", img: IMG.condomini },
  { l: "GALLERIA 2 — INDOOR", r: "4/3", img: IMG.solution },
  { l: "REAL — LOCKER LOBBY", r: "3/4", img: IMG.gallery[0] },
  { l: "GALLERIA 4 — CONDOMINIO", r: "4/3", img: IMG.hero },
  { l: "REAL — LOCKER OFFICE", r: "3/4", img: IMG.gallery[1] },
  { l: "GALLERIA 6 — CORRIERE", r: "4/3", img: IMG.consegna },
  { l: "GALLERIA 7 — DASHBOARD", r: "4/3", img: IMG.dashboard },
  { l: "GALLERIA 8 — INSTALLAZIONE", r: "3/4", img: IMG.installazione },
];
export function Gallery() {
  const { t } = useLang();
  return (
    <section className="bg-white py-24 sm:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionLabel>{t.gallery.label}</SectionLabel>
          <h2 className="font-sans text-4xl font-extrabold leading-tight tracking-tight text-ink sm:text-5xl lg:text-6xl">
            {t.gallery.title}
          </h2>
        </div>
        <div className="columns-1 gap-5 sm:columns-2 lg:columns-4">
          {galleryItems.map((g, i) => (
            <Reveal key={i} delay={(i % 4) * 0.06} className="mb-5 break-inside-avoid">
              {g.img
                ? <Media src={g.img} ratio={g.r} className="w-full" />
                : <Placeholder label={g.l} ratio={g.r} className="w-full" />}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- PILOT PROGRAM (ex Case Studies) ---------------- */
export function CaseStudies() {
  const { t } = useLang();
  return (
    <section className="bg-secondary py-24 sm:py-32" data-testid="cases-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-14 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <SectionLabel>{t.cases.label}</SectionLabel>
            <h2 className="font-sans text-4xl font-extrabold leading-tight tracking-tight text-ink sm:text-5xl lg:text-6xl">
              {t.cases.title}
            </h2>
          </div>
          <p className="max-w-md font-body text-lg leading-relaxed text-ink/70 lg:col-span-5">{t.cases.intro}</p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {t.cases.cards.map((c, i) => (
            <Reveal key={i} delay={i * 0.1} className="group overflow-hidden rounded-lg border border-border bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[8px_8px_0px_#0A0A0A]">
              <div className="p-3">
                {CASE_IMAGES[i]
                  ? <Media src={CASE_IMAGES[i]} alt={c.tag} ratio="16/9" className="w-full" />
                  : <Placeholder label={c.tag.toUpperCase()} ratio="16/9" tone="light" className="w-full" />}
              </div>
              <div className="p-6 pt-3">
                <span className="inline-block rounded-full bg-electric-blue px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider text-white">{c.tag}</span>
                <h3 className="mt-4 font-grotesk text-xl font-bold text-ink">{c.t}</h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-ink/60">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <button onClick={() => scrollTo("contact")} className="group inline-flex items-center gap-2 rounded-md bg-ink px-7 py-4 font-grotesk text-base font-bold text-white transition-colors duration-300 hover:bg-electric-blue" data-testid="pilot-cta">
            {t.cases.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
export function FAQ() {
  const { t } = useLang();
  return (
    <section id="faq" className="bg-white py-24 sm:py-32" data-testid="faq-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <SectionLabel>{t.faq.label}</SectionLabel>
            <h2 className="font-sans text-4xl font-extrabold leading-tight tracking-tight text-ink sm:text-5xl">
              {t.faq.title}
            </h2>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {t.faq.items.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-border" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="py-6 text-left font-grotesk text-lg font-bold text-ink hover:text-electric-blue hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 font-body text-base leading-relaxed text-ink/70">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- FINAL CTA ---------------- */
export function FinalCTA() {
  const { t } = useLang();
  return (
    <section className="bg-ink py-24 text-white sm:py-32" data-testid="final-cta-section">
      <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          <h2 className="font-display text-4xl font-black leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
            {t.ctaFinal.title}
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mx-auto mt-6 max-w-xl font-body text-lg text-white/70">{t.ctaFinal.sub}</p>
        </Reveal>
        <Reveal delay={0.3}>
          <motion.button
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            onClick={() => scrollTo("contact")}
            className="mt-10 inline-flex items-center gap-2 rounded-md bg-lime-accent px-9 py-4 font-grotesk text-lg font-extrabold text-ink"
            data-testid="final-cta-button"
          >
            {t.ctaFinal.btn}
            <ArrowUpRight className="h-5 w-5" />
          </motion.button>
        </Reveal>
      </div>
    </section>
  );
}
